/**
 * @fileoverview Reusable transient toast notification with auto-dismiss and a state hook.
 * @module ui_admin
 *
 * Responsibilities:
 * - Render a fixed top-right success/error/info toast pill (same visual pattern previously
 *   inlined in ContactsPage/LocationsPage — extracted here so it exists in one place).
 * - Provide useToastNotification, a hook owning toast state with auto-dismiss timers.
 *
 * Dependencies:
 * - lucide-react (CheckCircle2 / XCircle / Info / X icons)
 *
 * Architecture:
 * - Presentation-only block element; type is the modifier that drives color/icon.
 * - Hook keeps a single active toast; showing a new toast replaces the previous one
 *   and resets the auto-dismiss timer.
 */
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { CheckCircle2, Info, X, XCircle } from 'lucide-react';

/**
 * @contract ToastMessage
 *
 * Purpose:
 * - Describe a single transient toast.
 *
 * Fields:
 * - type: 'success' | 'error' | 'info' — drives color, icon, and default duration.
 * - message: Text shown in the toast body.
 *
 * Sync:
 * - Not applicable; ephemeral UI state.
 */
export interface ToastMessage {
  type: 'success' | 'error' | 'info';
  message: string;
}

export interface ToastNotificationProps {
  toast: ToastMessage | null;
  onDismiss: () => void;
}

/**
 * @component ToastNotification
 *
 * Purpose:
 * - Display a transient success/error/info notification in the top-right corner.
 *
 * Props:
 * - toast: The active toast, or null to render nothing.
 * - onDismiss: Invoked when the user clicks the dismiss button.
 *
 * Behavior:
 * - Success renders green with CheckCircle2; error renders red with XCircle;
 *   info renders blue with Info icon.
 * - aria-live polite status region for screen readers.
 *
 * Data Source:
 * - local (props only)
 *
 * Constraints:
 * - Presentation-only; auto-dismiss timing is owned by useToastNotification.
 *
 * @reusable
 *
 * Usage:
 * - Pair with useToastNotification: `const { toast, showToast, dismissToast } = useToastNotification();`
 *   then render `<ToastNotification toast={toast} onDismiss={dismissToast} />`.
 *
 * Rules:
 * - Stateless presentation-only component.
 */
export function ToastNotification({ toast, onDismiss }: ToastNotificationProps) {
  if (!toast) return null;
  return (
    <div
      role="status"
      aria-live="polite"
      data-testid={`toast-notification-${toast.type}`}
      className={`fixed top-4 right-4 z-100 max-w-md flex items-start gap-2 px-4 py-3 rounded-lg shadow-lg border text-sm ${
        toast.type === 'success'
          ? 'bg-green-50 border-green-200 text-green-800'
          : toast.type === 'info'
            ? 'bg-blue-50 border-blue-200 text-blue-800'
            : 'bg-red-50 border-red-200 text-red-800'
      }`}
    >
      {toast.type === 'success'
        ? <CheckCircle2 className="h-4 w-4 mt-0.5 shrink-0 text-green-600" />
        : toast.type === 'info'
          ? <Info className="h-4 w-4 mt-0.5 shrink-0 text-blue-600" />
          : <XCircle className="h-4 w-4 mt-0.5 shrink-0 text-red-600" />}
      <span className="flex-1">{toast.message}</span>
      <button
        type="button"
        onClick={onDismiss}
        className="ml-2 -mt-1 -mr-1 p-1 rounded-sm hover:bg-black/5"
        aria-label="Dismiss"
      >
        <X className="h-4 w-4" />
      </button>
    </div>
  );
}

const SUCCESS_DURATION_MS = 5000;
const INFO_DURATION_MS = 4000;
const ERROR_DURATION_MS = 7000;

/**
 * @hook useToastNotification
 *
 * Purpose:
 * - Own toast state and auto-dismiss timing for ToastNotification consumers.
 *
 * Inputs:
 * - None.
 *
 * Outputs:
 * - toast: The active ToastMessage or null.
 * - showToast(type, message): Show a toast (replaces any active one, resets the timer).
 * - dismissToast(): Clear the active toast immediately.
 *
 * Side Effects:
 * - Schedules a dismiss timeout (5s success / 4s info / 7s error); cleared on unmount or replacement.
 *
 * Dependencies:
 * - React state + timers only.
 */
export function useToastNotification(): {
  toast: ToastMessage | null;
  showToast: (type: ToastMessage['type'], message: string) => void;
  dismissToast: () => void;
} {
  const [toast, setToast] = useState<ToastMessage | null>(null);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const dismissToast = useCallback(() => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
    setToast(null);
  }, []);

  const showToast = useCallback((type: ToastMessage['type'], message: string) => {
    if (timerRef.current) clearTimeout(timerRef.current);
    setToast({ type, message });
    timerRef.current = setTimeout(() => {
      timerRef.current = null;
      setToast(null);
    }, type === 'success' ? SUCCESS_DURATION_MS : type === 'info' ? INFO_DURATION_MS : ERROR_DURATION_MS);
  }, []);

  useEffect(() => () => {
    if (timerRef.current) clearTimeout(timerRef.current);
  }, []);

  return { toast, showToast, dismissToast };
}

export default ToastNotification;
